// ReportStudentTable.jsx
import React, { useState } from "react";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import "@szhsin/react-menu/dist/transitions/slide.css";
import loader from "../../assets/loader.gif";
import { useNavigate } from "react-router-dom";
import { makePutRequest } from "../../utilities/common";
import { defaultUrl } from "../../features/actions";
import CustomModal from "../CustomModal/CustomModal";
import { useDispatch } from "react-redux";
import { setSharedState } from "../../features/sharedStateSlices/shareState";
import { FaChevronDown, FaFileUpload, FaEye } from "react-icons/fa";
import { User } from "lucide-react";

export const ReportStudentTable = ({
  classrooms,
  studentData,
  isLoading,
  setEditForm,
  setEditModeType,
  getEmergencyContact,
  classroomId: classroom_id,
  handleArchieve,
}: any) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12 bg-white rounded-2xl border border-slate-100 shadow-sm">
        <img
          src={loader}
          alt="Loading parameters..."
          className="w-12 h-12 object-contain"
        />
      </div>
    );
  }

  if (!studentData || studentData.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-2xl border border-slate-100 shadow-sm text-slate-400 font-medium text-sm italic">
        No dynamic student profiles registered on active system view window...
      </div>
    );
  }

  return (
    <>
      {/* Structural Card Container Layer */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden w-full">
        <div className="overflow-x-auto w-full">
          <table className="w-full border-collapse text-left min-w-[700px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                <th className="py-4 px-6 w-[15%] text-center">Avatar</th>
                <th className="py-4 px-4 w-[35%]">Student Full Name</th>
                <th className="py-4 px-4 w-[20%]">Date of Birth</th>
                <th className="py-4 px-4 w-[15%]">Classroom Assignment</th>
                <th className="py-4 px-6 w-[15%] text-center">Operation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {studentData.map((item: any, index: any) => {
                const fullName = `${item.firstname ?? ""} ${item.othername ? item.othername : ""} ${item.surname}`;

                return (
                  <tr
                    key={index}
                    className="group bg-white hover:bg-slate-50/40 transition-colors text-[13px] font-semibold text-slate-600"
                  >
                    {/* Column Field: Profile Avatar Icon Image */}
                    <td className="py-3 px-6 text-center">
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400 font-bold border-2 border-slate-50 overflow-hidden shadow-inner uppercase text-sm">
                        {item?.profile_picture &&
                          String(item.profile_picture).trim() !== "" ? (
                          typeof item.profile_picture === "string" ? (
                            <img
                              src={item.profile_picture}
                              alt={fullName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            item.profile_picture
                          )
                        ) : (
                          fullName.slice(0, 2)
                        )}
                      </div>
                    </td>

                    {/* Column Field: Full Name Descriptor text field */}
                    <td className="py-3 px-4 font-bold text-[#012063] tracking-tight">
                      {fullName}
                    </td>

                    {/* Column Field: Date of Birth String Format template */}
                    <td className="py-3 px-4 font-mono text-slate-500 text-xs">
                      {item.date_of_birth ? item.date_of_birth : "—"}
                    </td>

                    {/* Column Field: Assigned Classroom tag label metric */}
                    <td className="py-3 px-4">
                      <div className="flex flex-col items-start gap-1">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md font-sans font-bold bg-[#012063]/5 text-[#012063] text-xs">
                          {item.classroom?.name ?? "Unassigned"}
                        </span>
                        {item.classroom?.assessment_mode === "traits_only" ||
                        item.classroom?.assessment_type === "traits_only" ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-black bg-amber-50 text-amber-700 border border-amber-200">
                            🌟 Traits Only
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-50 text-sky-700 border border-sky-200">
                            Standard Matrix
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Column Field: Structural Action Options Menu Button Trigger */}
                    <td className="py-3 px-6 text-center">
                      <Menu
                        align="end"
                        transition
                        menuButton={
                          <MenuButton className="inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 bg-[#0096D1] hover:bg-[#007ba8] text-white text-xs font-bold shadow-sm transition-all active:scale-95">
                            Options{" "}
                            <FaChevronDown className="text-[9px] opacity-80" />
                          </MenuButton>
                        }
                        menuClassName="border border-slate-100 bg-white rounded-xl shadow-xl p-1.5 min-w-[160px] text-left"
                      >
                        <MenuItem
                          onClick={() => {
                            const rawPix = item?.profile_picture || item?.passport_photo || item?.image || "";
                            const pix = typeof rawPix === "string" && rawPix !== "undefined" && rawPix !== "null" ? rawPix.trim() : "";
                            const prefix = window.location.pathname.startsWith("/admin") ? "/admin" : "/staff";
                            const classroomObj = item?.classroom;
                            const detectedMode =
                              classroomObj?.assessment_mode ||
                              classroomObj?.assessment_type ||
                              (classroomObj?.subjects && classroomObj.subjects.length === 0 ? "traits_only" : "standard_matrix");
                            navigate(
                              `${prefix}/upload-result/${item.id}/${item?.classroom_id}`,
                              {
                                state: {
                                  student_name: `${item?.surname ?? ""} ${item?.firstname ?? ""}`.trim(),
                                  studentPix: pix,
                                  classroom: classroomObj,
                                  assessment_mode: detectedMode,
                                },
                              },
                            );
                          }}
                          className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 hover:text-[#0096D1] rounded-lg cursor-pointer transition-colors"
                        >
                          <FaFileUpload className="text-slate-400" /> Upload
                          Result
                        </MenuItem>

                        <MenuItem
                          onClick={() => {
                            const rawPix = item?.profile_picture || item?.passport_photo || item?.image || "";
                            const pix = typeof rawPix === "string" && rawPix !== "undefined" && rawPix !== "null" ? rawPix.trim() : "";
                            const prefix = window.location.pathname.startsWith("/admin") ? "/admin" : "/staff";
                            navigate(`${prefix}/view-result/${item.id}`, {
                              state: {
                                student_name: `${item?.surname ?? ""} ${item?.firstname ?? ""}`.trim(),
                                studentPix: pix,
                              },
                            });
                          }}
                          className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 hover:text-[#0096D1] rounded-lg cursor-pointer transition-colors"
                        >
                          <FaEye className="text-slate-400" /> View Result
                        </MenuItem>
                      </Menu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
